/**
 * Client Management Page
 * Manage deployed client apps: tokens, API keys, recharge, revoke
 */

import React, { useEffect, useState } from 'react';
import { Key, RefreshCw, Zap, Trash2, Plus, AlertTriangle, Check, Download, Globe, X, ChevronDown, ChevronRight, Cloud, ExternalLink, Play, Square } from 'lucide-react';
import Header from '../components/Layout/Header';
import { deploymentService } from '../services/api';
import { DeploymentClient } from '../types';
import toast from 'react-hot-toast';

const ClientManagementPage: React.FC = () => {
  const [clients, setClients] = useState<DeploymentClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [rechargeClientId, setRechargeClientId] = useState<string | null>(null);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', organization: '' });
  const [adding, setAdding] = useState(false);
  const [domainsExpandedId, setDomainsExpandedId] = useState<string | null>(null);
  const [editingDomains, setEditingDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [savingDomains, setSavingDomains] = useState(false);
  const [deployingClientId, setDeployingClientId] = useState<string | null>(null);
  const [cloudCredentials, setCloudCredentials] = useState<Record<string, { username: string; password: string }>>({});

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await deploymentService.getClients();
      setClients(data);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async (clientId: string) => {
    const amount = Number(rechargeAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await deploymentService.rechargeTokens(clientId, amount);
      toast.success(`Recharged ${amount} tokens`);
      setRechargeClientId(null);
      setRechargeAmount('');
      loadClients();
    } catch (e: any) {
      toast.error(e.message || 'Recharge failed');
    }
  };

  const handleRegenerateKey = async (clientId: string) => {
    if (!window.confirm('Regenerate API key? The old key will stop working immediately.')) return;
    try {
      await deploymentService.regenerateKey(clientId);
      toast.success('API key regenerated');
      loadClients();
    } catch (e: any) {
      toast.error(e.message || 'Failed');
    }
  };

  const handleDelete = async (clientId: string, name: string) => {
    if (!window.confirm(`Delete client "${name}"? This cannot be undone.`)) return;
    try {
      await deploymentService.deleteClient(clientId);
      toast.success('Client deleted');
      loadClients();
    } catch (e: any) {
      toast.error(e.message || 'Failed');
    }
  };

  const handleResetDevices = async (clientId: string, name: string) => {
    if (!window.confirm(`Reset all trusted devices for "${name}"?\n\nThis will clear all 5 device slots. The user will need to login again and re-register their devices (up to 5).`)) return;
    try {
      await deploymentService.resetDevices(clientId);
      toast.success(`Devices reset for ${name}. User can now register up to 5 devices.`);
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Failed to reset devices');
    }
  };

  const handleAddClient = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setAdding(true);
    try {
      await deploymentService.createClient(addForm);
      toast.success(`Client "${addForm.name}" added`);
      setAddForm({ name: '', email: '', phone: '', organization: '' });
      setShowAddForm(false);
      loadClients();
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Failed to add client');
    } finally {
      setAdding(false);
    }
  };

  const handleDownloadPackage = async (clientId: string) => {
    try {
      toast.loading('Building & launching...', { id: 'build' });
      const result = await deploymentService.buildAndDownload(clientId, 'web');
      toast.dismiss('build');
      if (result.success && result.downloadUrl) {
        const API = process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in';
        // Download
        const a = document.createElement('a');
        a.href = `${API}${result.downloadUrl}`;
        a.download = result.fileName || 'deployment-package.tar.gz';
        a.click();
        // Launch
        try {
          const launch = await deploymentService.launchBuild((result as any).buildId || '');
          if (launch.success && launch.url) {
            window.open(launch.url, '_blank');
            toast.success(`🚀 Launched at ${launch.url}`);
          } else {
            toast.success('Package downloaded!');
          }
        } catch {
          toast.success('Package downloaded!');
        }
      } else {
        toast.error(result.error || 'Build failed');
      }
    } catch (e: any) {
      toast.dismiss('build');
      toast.error(e.message || 'Failed');
    }
  };

  const handleExpandDomains = (client: DeploymentClient) => {
    if (domainsExpandedId === client.id) {
      setDomainsExpandedId(null);
      setEditingDomains([]);
      setNewDomain('');
    } else {
      setDomainsExpandedId(client.id);
      setEditingDomains(client.contract?.allowedOrigins || []);
      setNewDomain('');
    }
  };

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    // Basic domain validation
    if (!/^[a-z0-9]+([\-.][a-z0-9]+)*\.[a-z]{2,}$/.test(domain)) {
      toast.error('Enter a valid domain (e.g., example.com)');
      return;
    }
    if (editingDomains.includes(domain)) {
      toast.error('Domain already added');
      return;
    }
    setEditingDomains([...editingDomains, domain]);
    setNewDomain('');
  };

  const handleRemoveDomain = (domain: string) => {
    setEditingDomains(editingDomains.filter(d => d !== domain));
  };

  const handleSaveDomains = async (clientId: string) => {
    setSavingDomains(true);
    try {
      await deploymentService.updateAllowedOrigins(clientId, editingDomains);
      toast.success('Allowed domains updated');
      setDomainsExpandedId(null);
      setNewDomain('');
      loadClients();
    } catch (e: any) {
      toast.error(e.message || 'Failed to update domains');
    } finally {
      setSavingDomains(false);
    }
  };

  const handleCancelDomains = () => {
    setDomainsExpandedId(null);
    setEditingDomains([]);
    setNewDomain('');
  };

  const handleDeployToCloud = async (clientId: string) => {
    if (!window.confirm('Deploy this client app to Google Cloud? This will build and launch it.')) return;
    setDeployingClientId(clientId);
    try {
      toast.loading('Building & deploying to cloud...', { id: 'cloud-deploy' });
      const result = await deploymentService.deployToCloud(clientId);
      toast.dismiss('cloud-deploy');
      if (result.success) {
        toast.success(`☁️ Deployed at ${result.url}`);
        if (result.credentials) {
          setCloudCredentials(prev => ({ ...prev, [clientId]: result.credentials! }));
        }
        loadClients();
      } else {
        toast.error(result.error || 'Deploy failed');
      }
    } catch (e: any) {
      toast.dismiss('cloud-deploy');
      toast.error(e.response?.data?.error || e.message || 'Deploy failed');
    } finally {
      setDeployingClientId(null);
    }
  };

  const handleStopCloud = async (clientId: string) => {
    try {
      await deploymentService.stopCloud(clientId);
      toast.success('App stopped');
      loadClients();
    } catch (e: any) {
      toast.error(e.message || 'Failed to stop');
    }
  };

  const handleRestartCloud = async (clientId: string) => {
    try {
      await deploymentService.restartCloud(clientId);
      toast.success('App restarted');
      loadClients();
    } catch (e: any) {
      toast.error(e.message || 'Failed to restart');
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <Header title="Client Management" subtitle="Manage tokens, API keys, and deployed apps" />

      <div className="flex-1 overflow-y-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{clients.length}</div>
            <div className="text-xs opacity-80">Total Clients</div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{clients.reduce((sum, c) => sum + (c.tokens?.total || 0), 0).toLocaleString()}</div>
            <div className="text-xs opacity-80">Total Tokens Assigned</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{clients.reduce((sum, c) => sum + (c.tokens?.used || 0), 0).toLocaleString()}</div>
            <div className="text-xs opacity-80">Total Used</div>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{clients.reduce((sum, c) => sum + (c.tokens?.remaining || 0), 0).toLocaleString()}</div>
            <div className="text-xs opacity-80">Total Remaining</div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-pink-500 text-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold">{clients.filter(c => (c.tokens?.remaining || 0) <= 5).length}</div>
            <div className="text-xs opacity-80">Low Balance</div>
          </div>
        </div>

        {/* Client list */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Deployed Clients</h3>
            <button onClick={loadClients} className="text-xs text-gray-500 hover:text-primary flex items-center gap-1">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : clients.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <p className="font-medium">No clients deployed yet</p>
              <p className="text-xs mt-1">Use Deployment Studio to create client apps</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {clients.map(client => (
                <div key={client.id} className="p-4 hover:bg-gray-50/50">
                  <div className="flex items-start gap-4">
                    {/* Client info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-gray-900">{client.name}</span>
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${client.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {client.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {(client.tokens?.remaining || 0) <= 5 && (
                          <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex items-center gap-0.5">
                            <AlertTriangle size={8} /> Low tokens
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 space-x-3">
                        <span>✉️ {client.email}</span>
                        {client.phone && <span>📞 {client.phone}</span>}
                        {client.organization && <span>🏢 {client.organization}</span>}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1">
                        Agents: {client.assignedAgents?.join(', ') || 'None'} • Created: {new Date(client.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Token balance */}
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold text-gray-900">{(client.tokens?.remaining || 0).toLocaleString()}</div>
                      <div className="text-[9px] text-gray-400">tokens remaining</div>
                      <div className="text-[9px] text-gray-400">{(client.tokens?.used || 0).toLocaleString()} used / {(client.tokens?.total || 0).toLocaleString()} total</div>
                      <div className="flex items-center gap-2 mt-1 justify-end">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium" title="Tokens used via API key">
                          🔌 API: {(client.tokens?.apiUsed || 0).toLocaleString()}
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 font-medium" title="Tokens used via deployed App/Widget">
                          📱 App: {(client.tokens?.appUsed || 0).toLocaleString()}
                        </span>
                      </div>
                      {client.contract?.allowedOrigins && client.contract.allowedOrigins.length > 0 && (
                        <div className="text-[8px] text-blue-500 mt-0.5">🔒 {client.contract.allowedOrigins.join(', ')}</div>
                      )}
                    </div>
                  </div>

                  {/* API Key */}
                  <div className="mt-3 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <Key size={12} className="text-gray-400" />
                    <code className="text-[10px] font-mono text-gray-600 flex-1 select-all">{client.apiKey}</code>
                    <button onClick={() => { navigator.clipboard.writeText(client.apiKey); toast.success('API key copied!'); }} className="text-[9px] px-2 py-1 bg-gray-200 text-gray-700 rounded font-semibold hover:bg-gray-300" title="Copy API key">
                      📋 Copy
                    </button>
                    <button onClick={() => handleRegenerateKey(client.id)} className="text-[9px] px-2 py-1 bg-amber-100 text-amber-700 rounded font-semibold hover:bg-amber-200" title="Regenerate key (old key stops working)">
                      🔄 Regenerate
                    </button>
                  </div>

                  {/* Login Credentials */}
                  {(client.branding as any)?.credentials && (
                    <div className="mt-2 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <span className="text-[10px] text-blue-700 font-semibold">🔐 Login:</span>
                      <span className="text-[10px] font-mono text-blue-800">
                        User: <strong>{(client.branding as any).credentials.username}</strong>
                      </span>
                      <span className="text-[10px] font-mono text-blue-800">
                        Pass: <strong>{(client.branding as any).credentials.password}</strong>
                      </span>
                    </div>
                  )}

                  {/* Cloud Deployment Info */}
                  {client.cloudDeployment && (
                    <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Cloud size={12} className="text-emerald-600" />
                          <span className="text-[10px] font-semibold text-emerald-700">Cloud Deployed</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${client.cloudDeployment.isRunning ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {client.cloudDeployment.isRunning ? '● Running' : '○ Stopped'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {client.cloudDeployment.isRunning ? (
                            <button onClick={() => handleStopCloud(client.id)} className="text-[9px] px-2 py-0.5 bg-red-100 text-red-600 rounded font-semibold hover:bg-red-200" title="Stop app">
                              <Square size={8} className="inline mr-0.5" /> Stop
                            </button>
                          ) : (
                            <button onClick={() => handleRestartCloud(client.id)} className="text-[9px] px-2 py-0.5 bg-green-100 text-green-600 rounded font-semibold hover:bg-green-200" title="Restart app">
                              <Play size={8} className="inline mr-0.5" /> Start
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <a
                          href={client.cloudDeployment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] font-mono text-emerald-800 hover:text-emerald-600 underline flex items-center gap-1"
                        >
                          {client.cloudDeployment.url} <ExternalLink size={10} />
                        </a>
                        <button
                          onClick={() => { navigator.clipboard.writeText(client.cloudDeployment!.url); toast.success('URL copied!'); }}
                          className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold hover:bg-emerald-200"
                        >
                          📋
                        </button>
                      </div>
                      {client.cloudDeployment.customDomain && (
                        <div className="mt-1 text-[10px] text-emerald-600">
                          🌐 Custom: <span className="font-mono">{client.cloudDeployment.customDomain}</span>
                        </div>
                      )}
                      {/* Show credentials if available */}
                      {(cloudCredentials[client.id] || (client.branding as any)?.credentials) && (
                        <div className="mt-1.5 flex items-center gap-3 bg-white/60 rounded px-2 py-1">
                          <span className="text-[9px] text-emerald-700 font-semibold">🔑 App Login:</span>
                          <span className="text-[10px] font-mono text-emerald-800">
                            User: <strong>{cloudCredentials[client.id]?.username || (client.branding as any)?.credentials?.username || 'admin'}</strong>
                          </span>
                          <span className="text-[10px] font-mono text-emerald-800">
                            Pass: <strong>{cloudCredentials[client.id]?.password || (client.branding as any)?.credentials?.password || 'admin123'}</strong>
                          </span>
                        </div>
                      )}
                      {client.cloudDeployment.deployedAt && (
                        <div className="mt-1 text-[9px] text-emerald-500">
                          Deployed: {new Date(client.cloudDeployment.deployedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Contract info */}
                  {client.contract && (
                    <div className="mt-2 text-[10px] text-gray-500 flex gap-4">
                      <span>₹{client.contract.costPerToken}/token</span>
                      <span>One-time: ₹{client.contract.oneTimeCost}</span>
                      <span>Billing: {client.contract.billingCycle}</span>
                    </div>
                  )}

                  {/* Allowed Domains */}
                  <div className="mt-3">
                    <button
                      onClick={() => handleExpandDomains(client)}
                      className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600 hover:text-primary transition-colors"
                    >
                      {domainsExpandedId === client.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      <Globe size={12} />
                      Allowed Domains
                      {client.contract?.allowedOrigins && client.contract.allowedOrigins.length > 0 && (
                        <span className="ml-1 text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                          {client.contract.allowedOrigins.length}
                        </span>
                      )}
                    </button>

                    {domainsExpandedId === client.id && (
                      <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                        {/* Domain chips */}
                        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[28px]">
                          {editingDomains.length === 0 ? (
                            <span className="text-[10px] text-gray-400 italic">No domains configured — API accessible from any origin</span>
                          ) : (
                            editingDomains.map(domain => (
                              <span
                                key={domain}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 text-blue-700 rounded-full text-[10px] font-medium"
                              >
                                {domain}
                                <button
                                  onClick={() => handleRemoveDomain(domain)}
                                  className="text-blue-400 hover:text-red-500 transition-colors"
                                  title={`Remove ${domain}`}
                                >
                                  <X size={10} />
                                </button>
                              </span>
                            ))
                          )}
                        </div>

                        {/* Add domain input */}
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="text"
                            value={newDomain}
                            onChange={e => setNewDomain(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
                            placeholder="e.g., vidhiguru.in"
                            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                          />
                          <button
                            onClick={handleAddDomain}
                            className="px-2.5 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-[10px] font-semibold hover:bg-blue-100 flex items-center gap-1"
                          >
                            <Plus size={10} /> Add
                          </button>
                        </div>

                        {/* Save / Cancel */}
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                          <button
                            onClick={() => handleSaveDomains(client.id)}
                            disabled={savingDomains}
                            className="px-3 py-1.5 bg-primary text-white rounded-lg text-[10px] font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
                          >
                            {savingDomains ? 'Saving...' : <><Check size={10} /> Save</>}
                          </button>
                          <button
                            onClick={handleCancelDomains}
                            className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-[10px] font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex items-center gap-2">
                    {rechargeClientId === client.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={rechargeAmount}
                          onChange={e => setRechargeAmount(e.target.value)}
                          placeholder="Tokens to add"
                          className="px-2 py-1 border border-gray-200 rounded-lg text-xs w-32"
                          autoFocus
                        />
                        <button onClick={() => handleRecharge(client.id)} className="px-2 py-1 bg-green-600 text-white rounded-lg text-[10px] font-semibold">
                          <Check size={10} /> Add
                        </button>
                        <button onClick={() => { setRechargeClientId(null); setRechargeAmount(''); }} className="px-2 py-1 text-gray-400 text-[10px]">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button onClick={() => setRechargeClientId(client.id)} className="px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-[10px] font-semibold hover:bg-green-100 flex items-center gap-1">
                          <Plus size={10} /> Recharge
                        </button>
                        <button onClick={() => handleDownloadPackage(client.id)} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-[10px] font-semibold hover:bg-indigo-100 flex items-center gap-1">
                          <Download size={10} /> Build & Launch
                        </button>
                        <button
                          onClick={() => handleDeployToCloud(client.id)}
                          disabled={deployingClientId === client.id}
                          className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-[10px] font-semibold hover:bg-emerald-100 flex items-center gap-1 disabled:opacity-50"
                        >
                          <Cloud size={10} /> {deployingClientId === client.id ? 'Deploying...' : client.cloudDeployment ? 'Redeploy' : 'Deploy Cloud'}
                        </button>
                        <button onClick={() => handleRegenerateKey(client.id)} className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[10px] font-semibold hover:bg-amber-100 flex items-center gap-1">
                          <Key size={10} /> New Key
                        </button>
                        <button onClick={() => handleResetDevices(client.id, client.name)} className="px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-[10px] font-semibold hover:bg-purple-100 flex items-center gap-1">
                          <RefreshCw size={10} /> Reset Devices
                        </button>
                        <button onClick={() => handleDelete(client.id, client.name)} className="px-2.5 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg text-[10px] font-semibold hover:bg-red-100 flex items-center gap-1">
                          <Trash2 size={10} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientManagementPage;
